import {
  Alert,
  Button,
  PhoneInput,
  TextInput,
} from "@jugl-web/ui-components/cross-platform";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { isValidEmail } from "@jugl-web/utils/validators";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { UtilityTextInputBase } from "@jugl-web/ui-components/cross-platform/forms/UtilityTextInputBase";
import {
  saveItemToStorage,
  JUGL_LAST_LOGIN_METHOD,
} from "@jugl-web/utils/storage";
import LoginMethodPicker from "./components/LoginMethodPicker";
import PhoneMethodRadio from "./components/PhoneMethodRadio";
import { AuthStep, LoginMethod, PhoneMethod } from "../../types";
import { useLoginPage } from "../../providers/LoginPageProvider/useLoginPage";
import WhatsappAnimation from "./assets/whatsapp.json";

const LoginForm: React.FC = () => {
  const {
    loginMethod,
    phoneMethod,
    email,
    phone,
    setAuthStep,
    setPhoneMethod,
    setEmail,
    setPhone,
    requestOtpVerification,
  } = useLoginPage();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [isEmailError, setIsEmailError] = useState(false);
  const [isPhoneError, setIsPhoneError] = useState(false);
  const [whatsappAlertIsOpen, setWhatsappAlertIsOpen] = useState(false);
  const { t } = useTranslations();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleLogin = async () => {
    try {
      saveItemToStorage(JUGL_LAST_LOGIN_METHOD, loginMethod);
      await requestOtpVerification();
      setAuthStep(AuthStep.otp);
    } catch (error) {
      //
    }
  };

  const isError = useMemo(() => {
    if (loginMethod === LoginMethod.phone) {
      if (!phone.isValid) {
        setIsPhoneError(true);
      }
      return !phone.isValid;
    }
    return !isValidEmail(email);
  }, [loginMethod, email, phone.isValid]);

  const phoneInputValue = useMemo(
    () =>
      phone.code && phone.iso
        ? { phone: phone.phone, code: phone.code, iso: phone.iso }
        : undefined,
    [phone.code, phone.iso, phone.phone]
  );

  const subTitle = useMemo(() => {
    if (loginMethod === LoginMethod.email) {
      return t({
        id: "login-page.enter-email-to-send-otp-2",
        defaultMessage:
          "Just enter your email and we’ll send you your login code. It’s as easy as that!",
      });
    }
    return t({
      id: "login-page.enter-phone-to-send-otp-2",
      defaultMessage:
        "Enter your phone number and choose SMS or WhatsApp to get your login code. Quick and easy!",
    });
  }, [loginMethod, t]);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsFormSubmitted(true);
    if (isError) {
      return;
    }

    if (
      loginMethod === LoginMethod.phone &&
      phoneMethod === PhoneMethod.whatsapp
    ) {
      setWhatsappAlertIsOpen(true);
    } else {
      handleLogin();
    }
  };

  useEffect(() => {
    if (loginMethod === LoginMethod.email && emailInputRef.current) {
      emailInputRef.current?.focus();
    }
    if (loginMethod === LoginMethod.phone && phoneInputRef.current) {
      phoneInputRef.current?.focus();
    }
  }, [loginMethod, phoneInputValue, setPhone]);

  useEffect(() => {
    setIsFormSubmitted(false);
  }, [loginMethod]);

  const emailError = useMemo(() => {
    if (!isFormSubmitted) return undefined;
    if (email?.length === 0) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    if (!isEmailError) return undefined;
    return t({
      id: "form-controls.email.error-is-incorrect",
      defaultMessage: "Email is incorrect",
    });
  }, [email?.length, isEmailError, isFormSubmitted, t]);

  return (
    <>
      <form
        className="flex h-full w-full grow flex-col items-center justify-center font-secondary"
        onSubmit={handleFormSubmit}
      >
        <div className="flex flex-col gap-3 text-center">
          <span className="font-secondary text-[32px] font-medium text-dark">
            {t({
              id: "login-form.title",
              defaultMessage: "Hey there! Let’s get you started",
            })}
          </span>
          <span className="px-[64px] pb-[32px] text-sm text-grey-900 2xl:px-0 2xl:pb-[64px]">
            {subTitle}
          </span>
        </div>
        <div className="flex w-full max-w-[500px] flex-col justify-center">
          {loginMethod === LoginMethod.email ? (
            <TextInput
              ref={emailInputRef}
              placeholder={t({
                id: "login-page.email-input-placeholder",
                defaultMessage: "name@company.com",
              })}
              label={t({
                id: "login-form.email-input-label",
                defaultMessage: "Enter Your Work Email",
              })}
              isInvalid={Boolean(isEmailError)}
              isRequired
              value={email}
              errorMessage={emailError}
              onChange={(e) => {
                setIsEmailError(!isValidEmail(e.target.value));
                setEmail(e.target.value);
              }}
            />
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex max-h-[100px] flex-col">
                <UtilityTextInputBase
                  label={t({
                    id: "common.phone-number",
                    defaultMessage: "Phone Number",
                  })}
                  isInvalid={isFormSubmitted && isPhoneError}
                  isRequired
                >
                  <PhoneInput
                    maxLength={42}
                    ref={phoneInputRef}
                    value={phoneInputValue}
                    placeholder={t({
                      id: "form-controls.login-mobile-number.placeholder",
                      defaultMessage: "Enter Phone Number",
                    })}
                    onChange={(phoneNumber) => {
                      setIsPhoneError(!phoneNumber.isValid);
                      setPhone(phoneNumber);
                    }}
                    wrapperClassName="h-[56px] w-full"
                    hideVerticalLine
                  />
                </UtilityTextInputBase>
                {isFormSubmitted && isPhoneError && (
                  <span className="mt-1.5 text-sm leading-[140%] tracking-[0.14px] text-gradients-danger">
                    {phone.phone.length
                      ? t({
                          id: "login-page.invalid-phone-number",
                          defaultMessage: "Invalid phone number",
                        })
                      : t({
                          id: "login-page.phone-is-empty",
                          defaultMessage: "Phone number is empty",
                        })}
                  </span>
                )}
              </div>
              <div className="pt-3">
                <PhoneMethodRadio
                  checked={phoneMethod === PhoneMethod.sms}
                  onChange={() => setPhoneMethod(PhoneMethod.sms)}
                  title={t({
                    id: "login-page.receive-otp-sms",
                    defaultMessage: "Receive OTP by SMS",
                  })}
                />
              </div>
              <PhoneMethodRadio
                checked={phoneMethod === PhoneMethod.whatsapp}
                onChange={() => setPhoneMethod(PhoneMethod.whatsapp)}
                title={t({
                  id: "login-page.receive-otp-whatsapp",
                  defaultMessage: "Receive OTP by WhatsApp",
                })}
              />
            </div>
          )}
          <div
            className={cx(
              "flex flex-col gap-6",
              loginMethod === LoginMethod.email ? "pt-8" : "pt-[56px]"
            )}
          >
            <Button
              fullWidth
              type="submit"
              className="h-[40px] text-sm font-medium"
              isDisabled={
                (loginMethod === LoginMethod.phone && !phone.phone.length) ||
                (loginMethod === LoginMethod.email && !email.length)
              }
            >
              {t({
                id: "common.continue",
                defaultMessage: "Continue",
              })}
            </Button>
            {loginMethod === LoginMethod.email && (
              <div className="flex items-center gap-[25px] text-xs text-grey-700">
                <div className="h-px w-full bg-dark-100" />
                {t({
                  id: "common.or",
                  defaultMessage: "Or",
                })}
                <div className="h-px w-full bg-dark-100" />
              </div>
            )}
          </div>

          <div className="mt-auto pt-[24px]">
            <LoginMethodPicker />
          </div>
        </div>
      </form>
      <Alert
        isOpen={whatsappAlertIsOpen}
        onRequestClose={() => setWhatsappAlertIsOpen(false)}
        isCloseButtonVisible
        contentContainerClassName="pt-0"
        className="h-[437px] w-[448px]"
        header=" "
        headerClassName="border-b-0"
        backdropClassName="bg-dark/40 z-[51]"
        img={
          <Lottie
            options={{
              animationData: WhatsappAnimation,
            }}
            height={150}
            width={150}
          />
        }
        title={t({
          id: "login-page.confirm-access",
          defaultMessage: "Confirm Access",
        })}
        titleClassName="text-lg"
        content={
          <div className="text-sm">
            {t({
              id: "login-page.jugl-requires-whatsapp-permission-2",
              defaultMessage:
                "We need your permission to send you an OTP via WhatsApp",
            })}
          </div>
        }
        buttonsContainerClassName="flex justify-center [&>button]:flex-none mt-8"
        buttons={[
          {
            className: "w-[200px] h-[40px] float-center",
            text: t({
              id: "login-page.send-otp",
              defaultMessage: "Send OTP",
            }),
            onClick: () => {
              setWhatsappAlertIsOpen(false);
              handleLogin();
            },
          },
        ]}
      />
    </>
  );
};

export default LoginForm;
